/* SANIKB hero — rim light in WebGL. [Claude 2026-09-29]
   The boss on the SVG outline (a 3 px white stroke + blurred copies): "像小作坊" — he wants the real iPhone
   subject-lift look. Every outline now has a signed distance field (tools/bake_fields.py), and the light is computed
   per pixel: a hair-thin luminous edge that wraps a pixel into the product, a glow that falls off in three soft
   stages, a white-hot light front that runs round the contour from its top in both directions while the outline
   draws, one brighter pulse round the contour after a model swap, a slow breath while it rests, and a soft shadow
   under the lower edge so the product reads as lifted. Nothing washes over the product itself.
   Reads the state the hero script keeps on the SVG groups (.lg classes, data-fx / data-t0 / data-draw / data-s0); the SVG
   stays for clicks, dot placement and as the fallback when WebGL2 is not available. */
(() => {
  'use strict';
  const D = window.SANIKB_HERO_V9, cam = document.getElementById('cam'), hero = document.getElementById('hero');
  const liftRoot = document.getElementById('liftGroups');
  if (!D || !cam || !liftRoot) return;
  const [SW, SH] = D.size;
  const canvas = document.createElement('canvas');
  canvas.className = 'fx'; canvas.setAttribute('aria-hidden', 'true');
  const gl = canvas.getContext('webgl2', {alpha: true, premultipliedAlpha: true, antialias: false, depth: false, stencil: false});
  if (!gl) { window.__heroFX = {ok: false, renderer: 'svg', reason: 'WebGL unavailable; SVG contour retained.'}; return; }
  const svg = document.getElementById('lift');
  svg.after(canvas);

  const VS = `#version 300 es
in vec2 aP; uniform vec4 uR; uniform vec2 uSize; out vec2 vUV;
void main() { vUV = aP; vec2 r = uR.xy + aP * uR.zw;
  gl_Position = vec4(r.x / uSize.x * 2.0 - 1.0, 1.0 - r.y / uSize.y * 2.0, 0.0, 1.0); }`;
  // R8 remains byte-for-byte compatible; optional RG16 fields carry encoding/min/span metadata.
  // Decode the linearly filtered channels as one scalar: rounding channels would create byte seams.
  const FS = `#version 300 es
precision highp float;
in vec2 vUV; out vec4 outColor;
uniform sampler2D uF;
uniform vec3 uFormat;
uniform float uK, uS, uO, uBreath, uFlow;
float distanceAt(vec4 f) {
  if (uFormat.x > 1.5) return ((f.r * 65280.0 + f.g * 255.0) / 65535.0) * uFormat.z + uFormat.y;
  return f.r * 64.0 - 24.0;
}
void main() {
  float d = distanceAt(texture(uF, vUV)) * uK;
  float S = max(uS, 0.6);
  float aa = max(fwidth(d) * 0.65, 0.35);
  float rim = 1.0 - smoothstep(0.62 * S - aa, 0.62 * S + aa, abs(d - 0.4 * S));
  float halo = d > 0.0 ? exp(-d / (2.0 * S)) : 0.0;
  float phase = atan(vUV.y - 0.5, vUV.x - 0.5);
  float glint = pow(0.5 + 0.5 * cos(phase - uFlow * 6.2831853), 10.0);
  float L = ((0.32 + 0.15 * glint) * rim + 0.035 * halo) * uO * uBreath;
  L = clamp(L, 0.0, 0.90);
  outColor = vec4(vec3(1.0, 0.99, 0.975) * L, L);
}`;
  function sh(type, src) {
    const s = gl.createShader(type); gl.shaderSource(s, src); gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(s));
    return s;
  }
  let prog; const U = {};
  // [Claude 2026-10-19] the GL setup is a function: it runs again when the browser restores a lost context
  function setup() {
    prog = gl.createProgram();
    gl.attachShader(prog, sh(gl.VERTEX_SHADER, VS)); gl.attachShader(prog, sh(gl.FRAGMENT_SHADER, FS));
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(prog));
    gl.useProgram(prog);
    ['uR', 'uSize', 'uF', 'uFormat', 'uTx', 'uK', 'uS', 'uP', 'uHead', 'uShine', 'uO', 'uBreath', 'uShade', 'uFlow', 'uRest', 'uW', 'uW2'].forEach(n => U[n] = gl.getUniformLocation(prog, n));
    const buf = gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([0, 0, 1, 0, 0, 1, 1, 1]), gl.STATIC_DRAW);
    const aP = gl.getAttribLocation(prog, 'aP'); gl.enableVertexAttribArray(aP); gl.vertexAttribPointer(aP, 2, gl.FLOAT, false, 0, 0);
    gl.uniform2f(U.uSize, SW, SH); gl.uniform1i(U.uF, 0);
    gl.enable(gl.BLEND); gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
  }
  try { setup(); } catch (e) { canvas.remove(); window.__heroFX = {ok: false, error: String(e)}; return; }
  hero.classList.add('fx-on');

  // fields: data-fx = "room|group|model|basin" (basin only when the outline depends on it)
  const fieldOf = key => {
    const [sid, gid, m, b] = key.split('|');
    const g = D.scenes.find(s => s.id === sid)?.groups.find(x => x.id === gid);
    if (!g) return null;
    return (b && g.fieldsBy && g.fieldsBy[m] && g.fieldsBy[m][b]) || (g.fields && g.fields[m]) || null;
  };
  const tex = new Map();
  function texture(f) {
    let t = tex.get(f.src);
    if (t && t.failed && t.retryAt && performance.now() >= t.retryAt && (t.tries || 1) < 4) {   // [Claude 2026-10-19]
      tex.delete(f.src); try { gl.deleteTexture(t.tex); } catch (_) {} t = {tries: (t.tries || 1) + 1};
    } else if (t) return t.ready ? t : null;
    const tries = (t && t.tries) || 1;
    t = {ready: false, tex: gl.createTexture(), tries}; tex.set(f.src, t);
    const img = new Image(); t.image = img;
    if (!/^data:/.test(f.src)) img.crossOrigin = 'anonymous';   // theme build: fields are CDN files (served with ACAO *)
    img.onload = () => {
      if (tex.get(f.src) !== t) { if (t.objectUrl) { URL.revokeObjectURL(t.objectUrl); delete t.objectUrl; } delete t.image; return; }   // [Claude 2026-10-19] stale: the context was restored while this field downloaded
      try {
        const max = gl.getParameter(gl.MAX_TEXTURE_SIZE);
        if (!stats.ok || gl.isContextLost() || !img.naturalWidth || !img.naturalHeight || img.naturalWidth > max || img.naturalHeight > max) throw new Error('Invalid outline texture dimensions or unavailable context');
        for (let i = 0; i < 8 && gl.getError() !== gl.NO_ERROR; i++) {} // discard earlier unrelated errors
        gl.bindTexture(gl.TEXTURE_2D, t.tex);
        gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false); gl.pixelStorei(gl.UNPACK_COLORSPACE_CONVERSION_WEBGL, gl.NONE);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, img);
        if (gl.getError() !== gl.NO_ERROR) throw new Error('Outline texture upload failed');
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR); gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE); gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        if (gl.getError() !== gl.NO_ERROR) throw new Error('Outline texture setup failed');
        t.ready = true; t.w = img.naturalWidth; t.h = img.naturalHeight;
      } catch (e) {
        t.ready = false; t.failed = true;
        stats.errors.push('Outline texture upload failed; SVG fallback retained.');
      }
      if (t.objectUrl) { URL.revokeObjectURL(t.objectUrl); delete t.objectUrl; }
      delete t.image; kick();
    };
    const failed = () => {
      t.failed = true; t.ready = false; t.retryAt = performance.now() + 6000 * t.tries; setTimeout(kick, 6000 * t.tries + 50);
      if (t.objectUrl) { URL.revokeObjectURL(t.objectUrl); delete t.objectUrl; }
      delete t.image;
      stats.errors.push('Outline field failed to load; SVG fallback retained.'); kick();
    };
    img.onerror = failed;
    if (/^data:/.test(f.src)) img.src = f.src;
    else {
      // Distance values are data, so bypass Shopify's lossy WebP/AVIF image negotiation.
      fetch(f.src, {mode: 'cors', headers: {Accept: 'image/png'}}).then(response => {
        if (!response.ok || !/^image\/png(?:;|$)/i.test(response.headers.get('content-type') || '')) throw new Error('Lossless outline data unavailable');
        return response.blob();
      }).then(blob => {
        if (!stats.ok) throw new Error('Outline context unavailable');
        t.objectUrl = URL.createObjectURL(blob); img.src = t.objectUrl;
      }).catch(failed);
    }
    return null;
  }

  // rim, near/mid/far glow weights; wrap, ring shade, flow amount, ring radius (css px) — tuned on 2x crops of all rooms
  // rim, near/mid/far glow weights; wrap, top-light contrast, flow amount, contact cut; rest level after the sweep.
  // v13 (boss 9/30: the glow made products look like floating cut-outs): lit from above, no light on the resting
  // edge, smaller halo, and after each sweep the outline settles to a quiet rest level
  const TUNE = {w: [1.1, 0.75, 0.4, 0.14], w2: [0.35, 0.5, 0.4, 0.85], flowMs: 5200, rest: 0.72, settle: 1400};   // variant B of 3 (qa/canvas-na/fxv-*.png)
  const reduce = matchMedia('(prefers-reduced-motion: reduce)');
  const ease = t => t < .5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  const alpha = new Map();   // per group element: smoothed opacity
  const traced = new WeakSet();
  const stats = {ok: true, frames: 0, drawn: [], errors: [], renderer: 'progressive-svg-then-live-r8'};
  window.__heroFX = stats;
  let raf = 0, last = performance.now(), inView = true, ambT = 0;
  function kick() { if (!raf) raf = requestAnimationFrame(frame); }
  function target(n) {
    const c = n.classList;
    if (c.contains('is-fading')) return 0;
    if (c.contains('is-active')) return 1;
    if (c.contains('is-scan')) return .7;
    if (c.contains('is-peek')) return .55;
    return 0;
  }
  function resize() {
    // [Claude 2026-10-17] layout size, not the transformed box: the camera's arrival zoom used to reallocate the drawing
    // buffer every frame
    const r = {width: canvas.clientWidth || canvas.getBoundingClientRect().width, height: canvas.clientHeight || canvas.getBoundingClientRect().height};
    const dpr = Math.min(2, window.devicePixelRatio || 1, 3200 / Math.max(1, r.width), 1800 / Math.max(1, r.height));
    const w = Math.max(1, Math.round(r.width * dpr)), h = Math.max(1, Math.round(r.height * dpr));
    if (canvas.width !== w || canvas.height !== h) { canvas.width = w; canvas.height = h; }
    return {w, h, s: w / Math.max(1, r.width) * (parseFloat(getComputedStyle(hero).getPropertyValue('--lg-s')) || 1)};
  }
  function frame(now) {
    raf = 0;
    const dt = Math.min(100, now - last); last = now;
    if (!stats.ok || !inView || document.hidden) return;
    for (const n of alpha.keys()) if (!n.isConnected) alpha.delete(n);
    const {w, h, s} = resize();
    const worldH = Number(cam.dataset.worldHeight) || SH;
    gl.uniform2f(U.uSize, SW, worldH);
    gl.viewport(0, 0, w, h); gl.clearColor(0, 0, 0, 0); gl.clear(gl.COLOR_BUFFER_BIT);
    const K = w / SW, still = reduce.matches, calm = still || hero.dataset.playing === 'false';   // [Claude 2026-10-19] a11y-11: paused = no ambient motion
    const breath = calm ? 1 : .94 + .06 * Math.sin(now / 3400 * 2 * Math.PI);
    let busy = false, ambient = false; stats.drawn = [];
    for (const n of liftRoot.querySelectorAll('.lg')) {
      if (!still && n.classList.contains('is-tracing')) {
        alpha.set(n, 0); traced.add(n); busy = true;
        { const pf = n.dataset.fx && fieldOf(n.dataset.fx); if (pf) texture(pf); }   // [Claude 2026-10-17] fetch the field while the trace draws
        stats.drawn.push({g: n.dataset.g, fx: n.dataset.fx, o: 0,
          p: Math.max(0, Math.min(1, (now - Number(n.dataset.t0 || now)) / Math.max(1, Number(n.dataset.draw) || 1800))), phase: 'svg-trace'});
        continue;
      }
      const want = target(n);
      if (traced.has(n)) { alpha.set(n, 0); traced.delete(n); }   // [Claude 2026-10-17] fade in under the fading SVG trace (no snap)
      const was = alpha.get(n) ?? 0;
      const o = Math.abs(want - was) < .002 ? want : was + (want - was) * (1 - Math.exp(-dt / (want > was ? 120 : 90)));
      alpha.set(n, o);
      if (o !== want) busy = true;
      if (o < .003 || !n.dataset.fx) continue;
      const f = fieldOf(n.dataset.fx); if (!f) { n.dataset.fxReady = 'false'; continue; }
      const t = texture(f); if (!t) { n.dataset.fxReady = 'false'; if (!tex.get(f.src)?.failed) busy = true; continue; }
      const t0 = +n.dataset.t0 || 0, dr = +n.dataset.draw || 1150;
      let p = 1, head = 0, shine = -1;
      if (!still && t0 && now - t0 < dr + 250) {
        const q = Math.max(0, Math.min(1, (now - t0) / dr));
        p = ease(q); head = q < 1 ? 1 - Math.pow(q, 6) : Math.max(0, 1 - (now - t0 - dr) / 250); busy = true;
      }
      const s0 = +n.dataset.s0 || 0;
      if (!still && s0 && now >= s0 && now - s0 < 1100) { shine = ease((now - s0) / 1100); busy = true; }
      const W = window.__heroFXTune || TUNE;
      // full strength while the light runs round, then an ease down to the rest level
      const end = Math.max(t0 + dr, s0 ? s0 + 1100 : 0), since = now - end;
      let rest = W.rest;
      if (!still && t0 && since < W.settle) { rest = since <= 0 ? 1 : W.rest + (1 - W.rest) * (1 - ease(since / W.settle)); busy = true; }
      gl.bindTexture(gl.TEXTURE_2D, t.tex);
      gl.uniform4f(U.uR, f.x, f.y, f.w, f.h);
      gl.uniform3f(U.uFormat, f.encoding === 'rg16' ? 2 : 1, f.min ?? -24, f.span ?? 64);
      gl.uniform1f(U.uK, K); gl.uniform1f(U.uS, s); gl.uniform1f(U.uP, p); gl.uniform1f(U.uHead, head);
      gl.uniform1f(U.uShine, shine); gl.uniform1f(U.uO, o); gl.uniform1f(U.uBreath, breath);
      gl.uniform2f(U.uTx, 1 / (t.w || f.w / 2), 1 / (t.h || f.h / 2)); gl.uniform1f(U.uRest, rest);
      gl.uniform4f(U.uW, ...W.w); gl.uniform4f(U.uW2, ...W.w2);
      gl.uniform1f(U.uFlow, calm ? 0.25 : (now / (W.flowMs || 5200)) % 1);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      if (gl.getError() !== gl.NO_ERROR) {
        n.dataset.fxReady = 'false'; t.ready = false; t.failed = true;
        stats.errors.push('Outline field draw failed; SVG fallback retained.'); continue;
      }
      n.dataset.fxReady = 'true';
      stats.drawn.push({g: n.dataset.g, fx: n.dataset.fx, o: +o.toFixed(3), p: +p.toFixed(3), rest: +rest.toFixed(3)});
      if (!calm) ambient = true;   // the breath keeps an active outline alive (at ~30 fps, see below)
    }
    stats.frames++;
    // [Claude 2026-10-18] idle cost: fades, traces and sweeps run every frame; the slow breath / glint alone is
    // redrawn about 30 times a second (it changes too slowly to need 60)
    clearTimeout(ambT);
    if (busy) kick(); else if (ambient) ambT = setTimeout(kick, 28);
  }
  canvas.addEventListener('webglcontextlost', e => {
    e.preventDefault(); cancelAnimationFrame(raf); raf = 0;
    stats.ok = false; stats.renderer = 'svg'; stats.reason = 'WebGL context lost; SVG contour retained.';
    hero.classList.remove('fx-on'); canvas.style.display = 'none';
    liftRoot.querySelectorAll('.lg').forEach(n => { n.dataset.fxReady = 'false'; });
  });
  canvas.addEventListener('webglcontextrestored', () => {   // [Claude 2026-10-19] come back from the SVG fallback
    try { setup(); } catch (e) { stats.errors.push('WebGL restore failed; SVG contour retained.'); return; }
    tex.clear(); alpha.clear(); stats.ok = true; stats.renderer = 'progressive-svg-then-live-r8'; delete stats.reason;
    canvas.style.display = ''; hero.classList.add('fx-on'); kick();
  });
  new MutationObserver(kick).observe(liftRoot, {subtree: true, childList: true, attributes: true, attributeFilter: ['class', 'data-t0', 'data-s0', 'data-fx']});
  new MutationObserver(kick).observe(cam, {attributes: true, attributeFilter: ['data-world-height']});
  new MutationObserver(kick).observe(hero, {attributes: true, attributeFilter: ['data-playing']});   // [Claude 2026-10-19] pause / play redraws once
  addEventListener('resize', kick, {passive: true});
  if (reduce.addEventListener) reduce.addEventListener('change', kick); else reduce.addListener(kick);
  document.addEventListener('visibilitychange', kick);
  if ('IntersectionObserver' in window) new IntersectionObserver(es => { inView = es[es.length - 1].isIntersecting; kick(); }).observe(hero);
  kick();
})();
